import QRCode from "qrcode";
import { RecordableVersionBarcode } from "./types";

const escapeHtml = (value: string) =>
  value.replace(/[&<>'"]/g, (character) => {
    const entities: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;"
    };
    return entities[character];
  });

const qrCodeSvg = async (value: string) => {
  const svg = await QRCode.toString(value, {
    type: "svg",
    errorCorrectionLevel: "H",
    margin: 4
  });

  return svg.replace("<svg", '<svg class="qr-code" role="img" aria-label="Version QR code"');
};

export const printVersionBarcode = async (orderNumber: string, barcode: RecordableVersionBarcode) => {
  const qrCode = await qrCodeSvg(barcode.barcodeValue);
  const description = barcode.additionalDescription?.trim();
  const expectedVersion = barcode.expectedVersion ? `v${barcode.expectedVersion}` : "next version";
  const card = `
    <section class="label">
      <div class="eyebrow">TITLEQ DOCUMENT QR CODE</div>
      <h1>${escapeHtml(barcode.typeDescription)}</h1>
      <div class="version-title">Upload Version</div>
      ${qrCode}
      <div class="barcode-value">${escapeHtml(barcode.barcodeValue)}</div>
      <div class="order">Order <strong>${escapeHtml(orderNumber)}</strong></div>
      <div class="expected">Expected version: <strong>${escapeHtml(expectedVersion)}</strong></div>
      ${description ? `<div class="additional-description">${escapeHtml(description)}</div>` : ""}
      <div class="destination">Uploaded document will be saved to ${escapeHtml(barcode.saveLocation)}</div>
      <div class="disclaimer">Version numbers are assigned when the document is processed. The final version may be higher if another version was uploaded after this QR code was printed.</div>
      <div class="instructions">Place this sheet at the front of the document before scanning.</div>
    </section>`;

  const printFrame = document.createElement("iframe");
  printFrame.setAttribute("title", "Print version QR code");
  printFrame.style.position = "fixed";
  printFrame.style.right = "0";
  printFrame.style.bottom = "0";
  printFrame.style.width = "1px";
  printFrame.style.height = "1px";
  printFrame.style.border = "0";
  printFrame.style.opacity = "0";
  document.body.appendChild(printFrame);

  const printDocument = printFrame.contentDocument;
  if (!printDocument || !printFrame.contentWindow) {
    printFrame.remove();
    throw new Error("The print window could not be opened.");
  }

  printDocument.open();
  printDocument.write(`<!doctype html>
    <html>
      <head>
        <title>Version QR Code - ${escapeHtml(orderNumber)}</title>
        <style>
          @page { size: letter portrait; margin: 0.5in; }
          * { box-sizing: border-box; }
          body { margin: 0; color: #161c24; font-family: Arial, sans-serif; }
          .label { min-height: 9.9in; display: flex; flex-direction: column; justify-content: center; }
          .eyebrow { color: #637381; font-size: 11pt; font-weight: 700; letter-spacing: 0.12em; }
          h1 { margin: 10px 0 4px; font-size: 24pt; line-height: 1.2; }
          .version-title { font-size: 15pt; font-weight: 700; color: #3f51b5; }
          .qr-code { display: block; width: 2.5in; height: 2.5in; margin: 30px auto 0; }
          .barcode-value { margin-top: 10px; text-align: center; font-family: Menlo, Consolas, monospace; font-size: 10pt; }
          .order { margin-top: 18px; text-align: center; font-size: 18pt; }
          .expected { margin-top: 10px; text-align: center; font-size: 13pt; }
          .additional-description { margin-top: 14px; text-align: center; font-size: 13pt; }
          .destination { margin-top: 22px; text-align: center; color: #637381; font-size: 11pt; }
          .disclaimer { margin-top: 14px; text-align: center; color: #637381; font-size: 9pt; line-height: 1.35; }
          .instructions { margin-top: 52px; padding-top: 20px; border-top: 1px solid #dfe3e8; color: #637381; text-align: center; font-size: 11pt; }
        </style>
      </head>
      <body>${card}</body>
    </html>`);
  printDocument.close();

  const cleanUp = () => window.setTimeout(() => printFrame.remove(), 500);
  printFrame.contentWindow.addEventListener("afterprint", cleanUp, { once: true });
  window.setTimeout(() => {
    printFrame.contentWindow?.focus();
    printFrame.contentWindow?.print();
  }, 100);
  window.setTimeout(cleanUp, 60_000);
};
